import type { CorrelationEnvironment } from "@conciergeco/observability-correlation";
import { normalizeStripePublishableKey } from "../stripe/publishable-key";

type PublicRuntimeConfig = {
  apiEnvironment: CorrelationEnvironment;
  apiOrigin: string;
  authOrigin: string;
  sentryDsn?: string;
  sentryEnvironment?: string;
  sentryRelease?: string;
  stripePublishableKey?: string;
};

const PUBLIC_RUNTIME_CONFIG_GLOBAL =
  "__CONCIERGECO_COWORKING_PUBLIC_RUNTIME_CONFIG__";
const invalidPublicRuntimeConfigMessage =
  "Coworking public runtime configuration is invalid.";
const missingPublicRuntimeConfigMessage =
  "Coworking public runtime configuration is not installed.";
const canonicalApiEnvironments = {
  development: true,
  local: true,
  preview: true,
  production: true,
  staging: true,
  test: true,
} as const satisfies Record<CorrelationEnvironment, true>;

function parseApiEnvironment(value: unknown): CorrelationEnvironment {
  if (
    typeof value === "string" &&
    Object.hasOwn(canonicalApiEnvironments, value)
  ) {
    return value as CorrelationEnvironment;
  }

  throw new Error(invalidPublicRuntimeConfigMessage);
}

function parseHttpOrigin(value: unknown) {
  if (typeof value !== "string") {
    throw new Error(invalidPublicRuntimeConfigMessage);
  }

  let parsed: URL;

  try {
    parsed = new URL(value);
  } catch {
    throw new Error(invalidPublicRuntimeConfigMessage);
  }

  const isOrigin =
    (parsed.protocol === "http:" || parsed.protocol === "https:") &&
    parsed.username === "" &&
    parsed.password === "" &&
    parsed.pathname === "/" &&
    parsed.search === "" &&
    parsed.hash === "";

  if (!isOrigin) {
    throw new Error(invalidPublicRuntimeConfigMessage);
  }

  return parsed.origin;
}

function parseOptionalNonEmptyString(value: unknown) {
  if (value === undefined || value === "") {
    return undefined;
  }

  if (typeof value !== "string" || value.trim() !== value) {
    throw new Error(invalidPublicRuntimeConfigMessage);
  }

  return value;
}

function parseOptionalStripePublishableKey(value: unknown) {
  if (value === undefined || value === "" || value === "REPLACE_ME") {
    return undefined;
  }

  if (typeof value !== "string") {
    throw new Error(invalidPublicRuntimeConfigMessage);
  }

  const normalized = normalizeStripePublishableKey(value);

  if (!normalized || normalized !== value) {
    throw new Error(invalidPublicRuntimeConfigMessage);
  }

  return normalized;
}

function parsePublicRuntimeConfig(value: unknown): PublicRuntimeConfig {
  if (typeof value !== "object" || value === null) {
    throw new Error(invalidPublicRuntimeConfigMessage);
  }

  const candidate = value as Record<PropertyKey, unknown>;

  const sentryDsn = parseOptionalNonEmptyString(candidate.sentryDsn);
  const sentryEnvironment = parseOptionalNonEmptyString(
    candidate.sentryEnvironment,
  );
  const sentryRelease = parseOptionalNonEmptyString(candidate.sentryRelease);
  const stripePublishableKey = parseOptionalStripePublishableKey(
    candidate.stripePublishableKey,
  );

  return {
    apiEnvironment: parseApiEnvironment(candidate.apiEnvironment),
    apiOrigin: parseHttpOrigin(candidate.apiOrigin),
    authOrigin: parseHttpOrigin(candidate.authOrigin),
    ...(sentryDsn ? { sentryDsn } : {}),
    ...(sentryEnvironment ? { sentryEnvironment } : {}),
    ...(sentryRelease ? { sentryRelease } : {}),
    ...(stripePublishableKey ? { stripePublishableKey } : {}),
  };
}

function readBrowserPublicRuntimeConfig(): PublicRuntimeConfig {
  const runtimeGlobal = globalThis as typeof globalThis &
    Record<string, unknown>;
  const value = runtimeGlobal[PUBLIC_RUNTIME_CONFIG_GLOBAL];

  if (value === undefined) {
    throw new Error(missingPublicRuntimeConfigMessage);
  }

  return parsePublicRuntimeConfig(value);
}

function serializePublicRuntimeConfigScript(value: unknown) {
  const publicConfig = parsePublicRuntimeConfig(value);
  const serialized = JSON.stringify(publicConfig).replace(
    /[<>&\u2028\u2029]/g,
    (character) =>
      `\\u${character.charCodeAt(0).toString(16).padStart(4, "0")}`,
  );

  return `globalThis[${JSON.stringify(PUBLIC_RUNTIME_CONFIG_GLOBAL)}]=${serialized}`;
}

export {
  PUBLIC_RUNTIME_CONFIG_GLOBAL,
  parseApiEnvironment,
  parseOptionalNonEmptyString,
  parsePublicRuntimeConfig,
  readBrowserPublicRuntimeConfig,
  serializePublicRuntimeConfigScript,
  type PublicRuntimeConfig,
};
